import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import {
  getCompanyProfileInfo,
  getCompanySetting,
  getJobRoles,
  getJobPositions,
  getDepartmentByCompanyId,
  getDepartmentUsersByCompanyId,
  getUserList,
  getMyCompanyDefault,
  getUserAuthority,
  getAuthorityRoles,
  getAuthorityRoleUsers,
  getReceivedInvitations,
  InvitationNotification,
} from '@mygpt/app';

// 导入GPT store以设置roles
import { useGPTStore } from '@/store';
import { deepMerge } from '@/utils/helper';

interface PilotStoreInterface {
  companyProfileInfo?: any | undefined;
  adminInfo?: any | undefined;
  jobRolesList?: any | undefined;
  jobPositionList?: any | undefined;
  companyId?: any | undefined;
  companyName?: string | undefined;
  companyDescription?: string | undefined;
  companySetting?: any | undefined;
  departmentList?: any | undefined;
  departmentUsersList?: any | undefined;
  userList?: any | undefined;
  usersRes?: any | undefined;
  noneDepartmentUsers?: any[] | undefined;
  userOgsId?: string;
  hasAdminConsoleAuthority?: boolean;
  userOrgEnabled?: boolean;
  needsDefaultPasswordChange?: boolean;
  unreadMessageCount: number;
  authorityRolesList?: any | undefined;
  departmentTree?: any | undefined;
  isDataReady: boolean;
  isDataLoading: boolean; // 新增：防止重复加载
  fetchCompanyProfileInfo: (params: { companyId: string }) => Promise<any>;
  fetchCompanySetting: (params: { companyId: string }) => Promise<any>;
  fetchJobRoles: (params: { lang: string }) => Promise<any>;
  fetchJobPosition: (params: { lang: string }) => Promise<any>;
  fetchCompanyId: () => Promise<any>;
  fetchDepartmentByCompanyId: (params: { companyId: string }) => Promise<any>;
  fetchUserList: (params: {
    currentPage: number;
    pageSize: number;
    companyId: string;
    inCompanyUserNameInfixLike?: string;
    enabled: boolean | null;
    userCompanyEnabled?: boolean;
  }) => Promise<any>;
  fetchAuthorityRoles: () => Promise<any>;
  fetchUnreadMessageCount: () => Promise<void>;
  fetchAllAdminData: (forceUpdate?: boolean) => Promise<void>;
  refreshDepartmentData: () => Promise<void>;
}
// 定义部门的类型
interface Department {
  id: string;
  name: string;
  children?: Department[];
  members?: any[];
  totalMembers?: number;
}

export const useAdminStore = Object.assign(
  create<PilotStoreInterface>()(
    immer<PilotStoreInterface>((set, get) => ({
      // 线上版本不需要权限检查，所以初始值设为false
      // 本地部署版会在fetchCompanyId中更新这个值
      hasAdminConsoleAuthority: false,
      needsDefaultPasswordChange: false,
      unreadMessageCount: 0,
      isDataReady: false,
      isDataLoading: false, // 新增：防止重复加载
      fetchCompanyId: () =>
        new Promise<any>((resolve, reject) => {
          console.log('[fetchCompanyId] start');

          getMyCompanyDefault()
            .then(({ data }) => {
              console.log('[fetchCompanyId] then', data);
              if (data.success && data.company) {
                set({
                  companyId: data.company.id,
                  companyName: data.company.name,
                  companyDescription: data.company.description,
                  userOgsId: data.userSecurity?.id || '',
                  needsDefaultPasswordChange: data.userSecurity?.mustChangePassword || false,
                });

                // 获取到companyId后调用getUserAuthority获取权限信息
                const companyId = data.company.id;
                console.log('[fetchCompanyId] Fetching authority data for companyId:', companyId);

                // 只调用getUserAuthority，fetchDepartmentByCompanyId由fetchAllAdminData统一处理
                getUserAuthority(companyId)
                  .then((authorityRes) => {
                    // 处理权限数据
                    const authorityData = authorityRes.data;
                    set({
                      hasAdminConsoleAuthority: authorityData.authorityCodes.includes('ADMIN_CONSOLE'),
                      userOrgEnabled: authorityData.userCompanyEnabled !== false,
                    });

                    // 从getUserAuthority接口获取sysRoleCodes并设置到GPT store
                    if (authorityData.sysRoleCodes && Array.isArray(authorityData.sysRoleCodes)) {
                      // 将sysRoleCodes转换为Role格式并设置到GPT store
                      const roles = authorityData.sysRoleCodes.map((roleCode: string) => ({
                        id: roleCode, // 使用roleCode作为id
                        code: roleCode,
                        name: roleCode, // 可以根据需要设置更友好的名称
                        enabled: true,
                      }));
                      useGPTStore.getState().setRoles(roles);
                      console.log('[fetchCompanyId] set roles from getUserAuthority:', roles);
                    }

                    console.log('[fetchCompanyId] Authority data fetched successfully');
                  })
                  .catch((err) => {
                    console.error('[fetchCompanyId] getUserAuthority error:', err);
                    // 即使getUserAuthority失败，也不影响其他功能
                    set({
                      hasAdminConsoleAuthority: false,
                      userOrgEnabled: undefined,
                    });
                  });
              } else if (data.success && data.disabledCompanies?.length > 0) {
                // 用户在企业中被禁用
                const disabledCompany = data.disabledCompanies[0];
                console.log('[fetchCompanyId] User disabled in company:', disabledCompany);
                set({
                  companyId: disabledCompany.id,
                  companyName: disabledCompany.name,
                  companyDescription: null,
                  hasAdminConsoleAuthority: false,
                  userOrgEnabled: false,
                  needsDefaultPasswordChange: false,
                });
              } else {
                // 用户没有加入任何组织（COMPANY_NOT_FOUND）或其他错误
                console.log('[fetchCompanyId] No company found or error:', data.error?.name);
                set({
                  companyId: null,
                  companyName: null,
                  companyDescription: null,
                  hasAdminConsoleAuthority: false,
                  userOrgEnabled: undefined,
                  needsDefaultPasswordChange: false,
                });

                if (data.error?.name === 'JWT_TOKEN_EXPIRED') {
                  console.log('JWT_TOKEN_EXPIRED');
                }
              }
              resolve(data);
            })
            .catch((err) => {
              console.error('[fetchCompanyId] error', err);
              // 网络错误等情况，也要清空组织信息
              set({
                companyId: null,
                companyName: null,
                companyDescription: null,
                hasAdminConsoleAuthority: false,
              });
              reject(err);
            });
        }),
      fetchCompanySetting: (params: { companyId: string }) =>
        new Promise<any>((resolve, reject) => {
          getCompanySetting(params.companyId).then(({ data }) => {
            console.log('fetchCompanySetting', data);
            set({
              companySetting: data?.companySetting || null,
            });
            resolve(data);
          });
        }),
      fetchCompanyProfileInfo: (params: { companyId: string }) =>
        new Promise<any>((resolve, reject) => {
          getCompanyProfileInfo(params.companyId).then(({ data }) => {
            console.log('fetchCompanyProfileInfo', data);
            if (data.success) {
              set({
                companyProfileInfo: data?.companyProfile || null,
              });
              resolve(data);
            }
          });
        }),

      fetchJobRoles: (params: { lang: string }) =>
        new Promise<any>((resolve, reject) => {
          getJobRoles(params).then(({ data }) => {
            if (!data) return;
            console.log('res', data);
            set({
              jobRolesList: Array.isArray(data.jobRoles) ? data.jobRoles : [],
            });
            resolve(data);
          });
        }),
      fetchJobPosition: (params: { lang: string }) =>
        new Promise<any>((resolve, reject) => {
          getJobPositions(params).then(({ data }) => {
            if (!data) return;
            set({
              jobPositionList: Array.isArray(data.jobPositions) ? data.jobPositions : [],
            });
            resolve(data);
          });
        }),
      fetchDepartmentByCompanyId: (params: { companyId: string }) =>
        new Promise<any>((resolve, reject) => {
          Promise.all([getDepartmentByCompanyId(params.companyId), getDepartmentUsersByCompanyId(params.companyId)])
            .then(([departmentRes, usersRes]) => {
              console.log('Department response:', departmentRes);
              console.log('Users response:', usersRes);
              console.log('isolatedUserIds:', usersRes?.isolatedUserIds);
              console.log('users:', usersRes?.users);

              set({
                usersRes: usersRes,
              });

              // 确保数据结构正确
              const departmentList = Array.isArray(departmentRes?.departments) ? departmentRes.departments : [];
              const departmentIdToUserIdsMap = usersRes?.departmentIdToUserIdsMap || {};
              const users = Array.isArray(usersRes?.users) ? usersRes.users : [];
              const noneDepartmentUserIds = Array.isArray(usersRes?.isolatedUserIds) ? usersRes.isolatedUserIds : [];

              // 处理未分配部门的用户
              const noneDepartmentUsers = noneDepartmentUserIds
                .map((userId) => users.find((user) => user.id === userId))
                .filter(Boolean);

              console.log('noneDepartmentUserIds:', noneDepartmentUserIds);
              console.log('noneDepartmentUsers:', noneDepartmentUsers);

              const mapUsersToDepartments = (departments: Department[]): Department[] => {
                return departments.map((department) => {
                  const userIds = departmentIdToUserIdsMap[department.id] || [];
                  const departmentUsers = userIds
                    .map((userId) => users.find((user) => user.id === userId))
                    .filter(Boolean);

                  const children = mapUsersToDepartments(department.children || []);
                  const totalMembers = departmentUsers.length + getTotalMembers(children);

                  return {
                    ...department,
                    members: departmentUsers,
                    children: children,
                    totalMembers: totalMembers,
                  };
                });
              };

              function getTotalMembers(departments: Department[]): number {
                // 确保departments是有效的数组
                if (!departments || !Array.isArray(departments) || departments.length === 0) {
                  return 0;
                }

                return departments.reduce((total, dept) => {
                  // 确保dept存在且有效
                  if (!dept) {
                    return total;
                  }

                  const memberCount = Array.isArray(dept.members) ? dept.members.length : 0;
                  const childrenMembers = dept.children ? getTotalMembers(dept.children) : 0;
                  return total + memberCount + childrenMembers;
                }, 0);
              }

              const departmentsWithUsers = mapUsersToDepartments(departmentList);

              set({
                departmentList: departmentsWithUsers,
                departmentUsersList: users,
                noneDepartmentUsers: noneDepartmentUsers,
              });

              console.log('Updated departments:', departmentsWithUsers);
              console.log('None department users:', noneDepartmentUsers);
              resolve(departmentsWithUsers);
            })
            .catch((error) => {
              console.error('Error in fetchDepartmentByCompanyId:', error);
              reject(error);
            });
        }),
      fetchUserList: (params: {
        currentPage: number;
        pageSize: number;
        companyId: string;
        inCompanyUserNameInfixLike?: string;
        enabled: boolean | null;
        userCompanyEnabled?: boolean;
      }) =>
        new Promise<any>(async (resolve, reject) => {
          const userListData = await getUserList(params);
          // 从authorityRolesList获取所有角色代码
          const allRoleCodes = get().authorityRolesList?.map((role: any) => role.code) || [];
          let roleData = null;
          if (allRoleCodes.length > 0) {
            try {
              roleData = await getAuthorityRoleUsers({ roleCodes: allRoleCodes });
            } catch (error) {
              console.error('Error fetching role data:', error);
            }
          }
          // 合并角色数据到用户列表
          const rolesList = get().authorityRolesList || [];
          const roleIdToUserIds = deepMerge(roleData?.roleIdToUserIds, roleData?.roleIdToDisabledUserIds) || {};
          const usersWithRoles = (Array.isArray(userListData.users) ? userListData.users : []).map((user: any) => {
            // 为每个用户合成 roles 字段
            let userRoles = rolesList.filter((role: any) => {
              const userIds = roleIdToUserIds[role.id.toString()] || [];
              return userIds.includes(user.id);
            });
            // 强制 roles 为数组
            if (!Array.isArray(userRoles)) {
              userRoles = userRoles ? [userRoles] : [];
            }
            return {
              ...user,
              roles: userRoles,
            };
          });
          const result = {
            ...userListData,
            users: usersWithRoles,
          };
          set({
            userList: result,
          });
          resolve(result);
        }),
      fetchAuthorityRoles: () =>
        new Promise<any>((resolve, reject) => {
          getAuthorityRoles().then((data) => {
            if (!data) return;
            set({
              authorityRolesList: Array.isArray(data.roles) ? data.roles : [],
            });
            resolve(data);
          });
        }),
      fetchUnreadMessageCount: async () => {
        try {
          const response = await getReceivedInvitations();
          const notifications: InvitationNotification[] = response.data || [];
          const pendingCount = notifications.filter((n) => n.invitation?.status === 'pending').length;
          set({ unreadMessageCount: pendingCount });
        } catch (err) {
          console.error('[fetchUnreadMessageCount] error:', err);
        }
      },
      fetchAllAdminData: async function (forceUpdate?: boolean) {
        // 防止重复调用
        if (!forceUpdate && (get().isDataLoading || get().isDataReady)) {
          console.log('[fetchAllAdminData] Skipping - already loading or ready');
          return;
        }

        set({ isDataLoading: true });

        try {
          console.log('[fetchAllAdminData] Starting data fetch...');

          // 1. 先获取公司ID
          await get().fetchCompanyId();
          const companyId = get().companyId;

          if (!companyId) {
            console.warn('[fetchAllAdminData] No companyId available');
            set({ isDataLoading: false });
            return;
          }

          // 2. 并行获取权限角色和部门数据
          console.log('[fetchAllAdminData] Fetching authority roles and department data...');
          await Promise.all([get().fetchAuthorityRoles(), get().fetchDepartmentByCompanyId({ companyId })]);

          // 3. 获取用户列表
          console.log('[fetchAllAdminData] Fetching user list...');
          await get().fetchUserList({ currentPage: 1, pageSize: 999999, companyId, enabled: null });

          // 4. 合并角色数据到部门树
          const userListWithRoles = Array.isArray(get().userList?.users) ? get().userList.users : [];

          function replaceMembersWithRoles(depts: any[]): any[] {
            if (!Array.isArray(depts)) {
              return [];
            }
            return depts.map((dept) => ({
              ...dept,
              members: Array.isArray(dept.members)
                ? dept.members.map((m: any) => userListWithRoles.find((u: any) => u.id === m.id) || m)
                : [],
              children: Array.isArray(dept.children) ? replaceMembersWithRoles(dept.children) : [],
            }));
          }

          const departmentTreeWithRoles = replaceMembersWithRoles(
            Array.isArray(get().departmentList) ? get().departmentList : []
          );

          // 确保 noneDepartmentUsers 也被更新
          const currentNoneDepartmentUsers = get().noneDepartmentUsers || [];
          const updatedNoneDepartmentUsers = currentNoneDepartmentUsers.map(
            (user: any) => userListWithRoles.find((u: any) => u.id === user.id) || user
          );

          set({
            departmentTree: departmentTreeWithRoles,
            noneDepartmentUsers: updatedNoneDepartmentUsers,
            isDataReady: true,
            isDataLoading: false,
          });

          console.log('[fetchAllAdminData] Data fetch completed successfully');
          console.log('[fetchAllAdminData] departmentTree:', departmentTreeWithRoles);
          console.log('[fetchAllAdminData] noneDepartmentUsers:', updatedNoneDepartmentUsers);
        } catch (error) {
          console.error('[fetchAllAdminData] Error during data fetch:', error);
          set({ isDataLoading: false });
          throw error;
        }
      },
      refreshDepartmentData: async function () {
        const companyId = get().companyId;
        if (!companyId) {
          console.warn('[refreshDepartmentData] No companyId available');
          return;
        }

        try {
          console.log('[refreshDepartmentData] Refreshing department data...');

          // 1. 重新获取部门列表
          await get().fetchDepartmentByCompanyId({ companyId });

          // 2. 重新获取用户列表（包含角色信息）
          await get().fetchUserList({ currentPage: 1, pageSize: 999999, companyId, enabled: null });

          // 3. 重新合并角色数据到部门树
          const userListWithRoles = Array.isArray(get().userList?.users) ? get().userList.users : [];

          function replaceMembersWithRoles(depts: any[]): any[] {
            if (!Array.isArray(depts)) {
              return [];
            }
            return depts.map((dept) => ({
              ...dept,
              members: Array.isArray(dept.members)
                ? dept.members.map((m: any) => userListWithRoles.find((u: any) => u.id === m.id) || m)
                : [],
              children: Array.isArray(dept.children) ? replaceMembersWithRoles(dept.children) : [],
            }));
          }

          const departmentTreeWithRoles = replaceMembersWithRoles(
            Array.isArray(get().departmentList) ? get().departmentList : []
          );

          // 4. 确保 noneDepartmentUsers 也被更新
          const currentNoneDepartmentUsers = get().noneDepartmentUsers || [];
          const updatedNoneDepartmentUsers = currentNoneDepartmentUsers.map(
            (user: any) => userListWithRoles.find((u: any) => u.id === user.id) || user
          );

          // 5. 更新 departmentTree 和 noneDepartmentUsers
          set({
            departmentTree: departmentTreeWithRoles,
            noneDepartmentUsers: updatedNoneDepartmentUsers,
          });

          console.log('[refreshDepartmentData] Department data refreshed successfully');
          console.log('[refreshDepartmentData] Updated departmentTree:', departmentTreeWithRoles);
          console.log('[refreshDepartmentData] Updated noneDepartmentUsers:', updatedNoneDepartmentUsers);
        } catch (error) {
          console.error('[refreshDepartmentData] Error refreshing department data:', error);
          throw error;
        }
      },
    }))
  ),
  { set: (updater: any) => useAdminStore.setState(updater) }
);
